///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2023
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { Fragment, useEffect, useState } from "react";
import { Route, Switch } from "react-router-dom";
import { Tab, TabList } from "react-tabs";
import { useSelector, useDispatch } from "react-redux";

import Summary from "../components/Bid/Summary";
import Details from "../components/Bid/Details";
import Pricing from "../components/Bid/Pricing";
import {
  toggleNewBidFileUploadError,
  closeInitialLoadErrorModal,
  fetchBidSummary,
  fetchBidMessages
} from "../slices/bidSummarySlice";
import {
  checkLaneImportInProgress,
  checkLaneRepriceInProgress,
  reset as resetPricing,
  showImportInProgressModal
} from "../slices/bidPricingSlice";
import Modal from "../components/Modal";
import Alert from "../components/Alert";
import Banner from "../components/Bid/Banner";
import BidInfo from "../components/Bid/BidInfo";
import isAuthorizedToView from "../helpers/isAuthorizedToView";
import messages from "../helpers/messages";
import {
  userRole,
  userRoleListForBidView,
  mode as modeType,
  repType,
  division,
  bidTypes
} from "../helpers/types";
import AutoComplete from "../components/form-controls/AutoComplete";
import { setUserRoleForManagementUser } from "../slices/userSlice";
import Procurement from "../components/Bid/Procurement";
import {
  checkCapacityMatchingInProgress,
  fetchCapacityMatchLaneSummary
} from "../slices/bidProcurementSlice";

const SUMMARY = "summary";
const DETAILS = "details";
const PRICING = "pricing";
const PROCUREMENT = "procurement";

let laneImportIntervalId = null;
let laneRepriceIntervalId = null;
let capacityMatchingIntervalId = null;

function Bid({ history, match, role, isManagement }) {
  const [showPricingTab, togglePricingTab] = useState(true);
  const [showProcurementTab, toggleProcurementTab] = useState(false);

  const { bidId } = match.params;
  const route = history.location.pathname.split("/")[3] || "";

  const path = match.path;

  const {
    bidSummary: { newBidFileUploadError, summary, initialLoadError },
    bidPricing: {
      isLaneImportInProgress,
      importInProgressModal,
      division: bidDivision,
      pricingProgress: { isLaneRepriceInProgress, repriceReason }
    },
    featureFlags: { enableInternalCapacityMatching },
    bidProcurement: { isCapacityMatchingInProgress }
  } = useSelector(state => state);

  const dispatch = useDispatch();

  useEffect(() => {
    if (isManagement && !userRoleListForBidView.find(x => x.value === role)) {
      dispatch(setUserRoleForManagementUser(userRole.tlPricingAnalyst));
    }
  }, [dispatch, isManagement, role]);

  useEffect(() => {
    if (summary?.modes) {
      const showPricing = isAuthorizedToView(role, summary.modes);
      togglePricingTab(showPricing);
      if (!showPricing && route === PRICING) {
        history.replace(match.url + "/" + SUMMARY);
      }
    }
  }, [role, summary, history, match.url, route]);

  useEffect(() => {
    if (summary?.modes) {
      const showProcurement =
        enableInternalCapacityMatching &&
        (role === userRole.carrierRelationsRepresentative ||
          role === userRole.procurementRepresentative ||
          (role === userRole.assetBasedPricingUser &&
            summary.bidTypeId ===
              bidTypes.find(
                x => x.label === messages.coyoteFleet.defaultMessage
              ).value)) &&
        summary.division === division.coy &&
        summary.modes.find(({ mode }) => mode === modeType.tl)
          ?.bidOwnerRepType === repType.pricing;
      toggleProcurementTab(showProcurement);
      if (!showProcurement && route === PROCUREMENT) {
        history.replace(match.url + "/" + SUMMARY);
      }
    }
  }, [
    role,
    summary,
    history,
    match.url,
    route,
    enableInternalCapacityMatching
  ]);

  useEffect(() => {
    const importInProgressPollingInterval = 5000;
    const callbackSuccess = value => {
      if (value) {
        laneImportIntervalId = setInterval(() => {
          if (isLaneImportInProgress && role) {
            dispatch(checkLaneImportInProgress(bidId));
          }
        }, importInProgressPollingInterval);
      }
    };
    dispatch(checkLaneImportInProgress(bidId, callbackSuccess));
  }, [dispatch, bidId, isLaneImportInProgress, role]);

  useEffect(() => {
    if (!isLaneImportInProgress && laneImportIntervalId) {
      clearInterval(laneImportIntervalId);
      dispatch(
        fetchBidSummary({
          bidId,
          role,
          fetchTLLaneCount: false,
          division: bidDivision,
          fetchLanes: route === PRICING,
          shouldFetchMatchingLanes: true
        })
      );
      dispatch(checkLaneRepriceInProgress(bidId));
    }
  }, [dispatch, bidId, isLaneImportInProgress, role, bidDivision, route]);

  useEffect(() => {
    const repriceInProgressPollingInterval = 5000;
    const callbackSuccess = value => {
      if (value) {
        laneRepriceIntervalId = setInterval(() => {
          if (isLaneRepriceInProgress && role) {
            dispatch(checkLaneRepriceInProgress(bidId));
          }
        }, repriceInProgressPollingInterval);
      }
    };
    dispatch(checkLaneRepriceInProgress(bidId, callbackSuccess));
  }, [dispatch, bidId, isLaneRepriceInProgress, role]);

  useEffect(() => {
    if (!isLaneRepriceInProgress && laneRepriceIntervalId) {
      clearInterval(laneRepriceIntervalId);
      dispatch(
        fetchBidSummary({
          bidId,
          role,
          fetchTLLaneCount: false,
          division: bidDivision,
          fetchLanes: route === PRICING,
          shouldFetchMatchingLanes: true
        })
      );
      route === "summary" && dispatch(fetchBidMessages(bidId));
    }
  }, [dispatch, bidId, isLaneRepriceInProgress, role, bidDivision, route]);

  useEffect(() => {
    const capacityMatchingInProgressPollingInterval = 5000;
    const callbackSuccess = value => {
      if (value) {
        capacityMatchingIntervalId = setInterval(() => {
          if (isCapacityMatchingInProgress && role) {
            dispatch(checkCapacityMatchingInProgress(bidId));
          }
        }, capacityMatchingInProgressPollingInterval);
      }
    };

    dispatch(checkCapacityMatchingInProgress(bidId, callbackSuccess));
  }, [dispatch, bidId, isCapacityMatchingInProgress, role]);

  useEffect(() => {
    if (!isCapacityMatchingInProgress && capacityMatchingIntervalId) {
      clearInterval(capacityMatchingIntervalId);
      route === "procurement" && dispatch(fetchCapacityMatchLaneSummary(bidId));
    }
  }, [dispatch, bidId, isCapacityMatchingInProgress, route]);

  const pageContainerClass =
    route === PRICING || route === PROCUREMENT ? "" : " page-container ";

  return (
    <Fragment>
      <div className={"pt-16 bid-view" + pageContainerClass}>
        <Modal
          title={messages.createNewBid.defaultMessage}
          open={newBidFileUploadError}
          handleClose={() => dispatch(toggleNewBidFileUploadError(false))}
          titleClass="modal__title_light uppercase"
          closeButtonClass="modal__close-button mr-16 mt-16"
          bodyClass="border-top-grey pl-24 pr-24 pt-16 pb-32"
        >
          <p className="univers-65-bold font-size-24 color-grey">
            {messages.attachmentError.defaultMessage}
          </p>
          <p
            data-testid="new-bid-attachment-error"
            className="mb-24 font-size-16"
          >
            {messages.newBidAttachmentUploadErrorMessage.defaultMessage}
          </p>
          <div className="mt-8 mb-16">
            &nbsp;
            <button
              onClick={() => dispatch(toggleNewBidFileUploadError(false))}
              className="primary-button fr univers-67-bold-cond"
            >
              {messages.goToBid.defaultMessage}
            </button>
          </div>
        </Modal>

        {importInProgressModal && (
          <Alert
            title={messages.processing.defaultMessage}
            message={messages.anotherImportInProgressMessage.defaultMessage}
            handleClose={() => dispatch(showImportInProgressModal(false))}
            open={importInProgressModal}
            large={true}
          />
        )}

        <Alert
          title={messages.systemError.defaultMessage}
          open={initialLoadError}
          handleClose={() => dispatch(closeInitialLoadErrorModal())}
          message={messages.bidViewErrorMessage.defaultMessage}
        ></Alert>

        <div
          className={
            "one-whole" +
            (route === PRICING || route === PROCUREMENT ? " px-24" : "")
          }
        >
          <BidInfo role={role} isManagement={isManagement} />
          <div className="fr">
            {summary && isManagement && (
              <div className="inline-block mr-12" data-testid="userRoleToView">
                <AutoComplete
                  label={messages.userRoleToView.defaultMessage}
                  staticOptions={
                    enableInternalCapacityMatching
                      ? userRoleListForBidView
                      : userRoleListForBidView.filter(
                          item =>
                            item.label !==
                              messages.carrierRelationsRepresentative
                                .defaultMessage &&
                            item.label !==
                              messages.procurementRepresentative.defaultMessage
                        )
                  }
                  value={userRoleListForBidView.find(x => x.value === role)}
                  handleChange={userRole => {
                    if (role !== userRole.value) {
                      dispatch(resetPricing());
                      dispatch(setUserRoleForManagementUser(userRole.value));
                    }
                  }}
                />
              </div>
            )}
            <TabList className="fr mb-0 bid-tab-list inline-block">
              <Tab
                selected={route === SUMMARY}
                onClick={() =>
                  route === SUMMARY || history.push(match.url + "/" + SUMMARY)
                }
              >
                {messages.summary.defaultMessage}
              </Tab>
              <Tab
                selected={route === DETAILS}
                onClick={() =>
                  route === DETAILS || history.push(match.url + "/" + DETAILS)
                }
              >
                {messages.details.defaultMessage}
              </Tab>
              {showPricingTab && (
                <Tab
                  selected={route === PRICING}
                  onClick={() =>
                    route === PRICING ||
                    history.push(`${match.url}/${PRICING}/lane-match`)
                  }
                >
                  {messages.pricing.defaultMessage}
                </Tab>
              )}
              {showProcurementTab && (
                <Tab
                  selected={route === PROCUREMENT}
                  onClick={() =>
                    route === PROCUREMENT ||
                    history.push(match.url + "/" + PROCUREMENT)
                  }
                >
                  {messages.procurement.defaultMessage}
                </Tab>
              )}
            </TabList>
          </div>
        </div>
      </div>
      <Banner
        pageContainerClass={pageContainerClass}
        isLaneImportInProgress={isLaneImportInProgress}
        isLaneRepriceInProgress={isLaneRepriceInProgress}
        isCapacityMatchingInProgress={isCapacityMatchingInProgress}
        repriceReason={repriceReason}
      />
      {role && (
        <Switch>
          <Route
            path={path + "/" + DETAILS}
            render={props => <Details {...props} role={role} />}
          />
          <Route
            path={path + "/" + PRICING}
            render={props => <Pricing {...props} role={role} />}
          />
          <Route
            path={path + "/" + SUMMARY}
            render={props => <Summary bidId={bidId} role={role} {...props} />}
          />
          <Route
            path={path + "/" + PROCUREMENT}
            render={props => (
              <Procurement bidId={bidId} role={role} {...props} />
            )}
          />
        </Switch>
      )}
    </Fragment>
  );
}

export default Bid;
